import { Injectable, signal } from '@angular/core';
import { timer } from 'rxjs';
import { ShellBackgroundEntityModel } from './shell-background-entity/shell-background-entity.model';

/**
 * How long a leaving background may take to fade out (the 500ms CSS transition
 * plus margin) before it is force-removed. The removal normally happens on
 * `transitionend`, but that event is not guaranteed (hidden tab, interrupted
 * paint) — without the fallback a leaked invisible entry would keep a muted
 * background video playing forever.
 */
const LEAVE_REMOVAL_FALLBACK_MS = 700;

/**
 * A background system currently on the display list.
 */
export interface ShellBackgroundSystem extends ShellBackgroundEntityModel {
  /**
   * Transient exit state: the system is fading out and will be removed when
   * its fade completes.
   */
  leaving: boolean;
}

/**
 * The app-wide background state used by the {@link ShellBackgroundComponent}.
 *
 * Backgrounds cross-fade: a new system is pushed on top of the current one and
 * fades in; once it has fully entered, the systems beneath it fade out and are
 * removed. A `null` configuration fades everything out.
 */
@Injectable({
  providedIn: 'root',
})
export class ShellBackgroundRepository {
  /**
   * The display list of background systems, oldest first.
   */
  private readonly systemsState = signal<ShellBackgroundSystem[]>([]);

  ////////////////////////////////////////////////////////////////////
  // WRITERS
  ////////////////////////////////////////////////////////////////////

  /**
   * Applies a validated configuration from the {@link ShellBackgroundService}.
   *
   * @param configuration - `undefined` leaves the background untouched (it
   * persists across screens); `null` fades out every system; a model is pushed
   * onto the display list and cross-fades in.
   */
  applyConfiguration(configuration?: ShellBackgroundEntityModel | null) {
    // if the configuration is specifically undefined, nothing
    // changes on the background because it persists.
    if (configuration === undefined) {
      return;
    }

    // if the configuration is specifically null, all systems fade out
    if (configuration === null) {
      this.markLeaving(this.systemsState().map((system) => system.id));
      return;
    }

    // otherwise the new system joins the display list
    this.systemsState.update((systems) => [...systems, { ...configuration, leaving: false }]);
  }

  /**
   * Occurs once a background has fully faded into place: every older system
   * beneath it begins its fade-out.
   *
   * @param id - the system whose entrance completed.
   */
  applyBackgroundEntered(id: string) {
    const systems = this.systemsState();
    const index = systems.findIndex((system) => system.id === id);
    if (index <= 0) {
      return;
    }
    this.markLeaving(
      systems
        .slice(0, index)
        .filter((system) => !system.leaving)
        .map((system) => system.id),
    );
  }

  /**
   * Occurs once a leaving background has fully faded out: it is removed from
   * the display list. Safe to call repeatedly (the `transitionend` handler and
   * the fallback timer may race).
   *
   * @param id - the system whose exit completed.
   */
  applyLeaveComplete(id: string) {
    this.systemsState.update((systems) => systems.filter((system) => !(system.id === id && system.leaving)));
  }

  /**
   * Marks the given systems as leaving and schedules their fallback removal.
   */
  private markLeaving(ids: string[]) {
    if (!ids.length) {
      return;
    }
    this.systemsState.update((systems) => systems.map((system) => (ids.includes(system.id) ? { ...system, leaving: true } : system)));
    for (const id of ids) {
      timer(LEAVE_REMOVAL_FALLBACK_MS).subscribe(() => this.applyLeaveComplete(id));
    }
  }

  ////////////////////////////////////////////////////////////////////
  // QUERIES
  ////////////////////////////////////////////////////////////////////

  /**
   * The display list of background systems, oldest first.
   */
  readonly systems = this.systemsState.asReadonly();
}
